// Gear button on the Dialer (next to the "Dialer" title, or in the collapsed rail)
// that opens a small anchored popover of agent dialer settings. Rendered only for agents
// the admin gave the switch to (agents.show_auto_answer_toggle); today it holds the Auto
// answer switch, more settings can be added later.
//
// Auto answer is usable only when the agent has a WebRTC telephony profile (whether or not
// it is logged in right now). Otherwise the row is greyed out and the reason is shown
// under it. The on/off is agents.auto_pick_enabled: first set by the admin, then the agent
// can change it here. A green dot on the gear means it's on.
import { useEffect, useState } from 'react';
import { Info, PhoneIncoming, Settings } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { useAgentAutoAnswer, useDialerPrefsStore } from '../stores/dialerPrefsStore';

const WEBRTC_ONLY_MESSAGE = 'Auto-answer is available only for WebRTC devices';

export default function DialerSettingsButton({
  agentId,
  hasWebrtcProfile,
  serverAutoAnswer,
  variant = 'header',
}: {
  agentId: string | number | null | undefined;
  /** The agent has a WebRTC telephony profile configured. */
  hasWebrtcProfile: boolean;
  /** Saved value from the login / dashboard agent data (agents.auto_pick_enabled). */
  serverAutoAnswer: boolean;
  /** header: next to the Dialer title (opens below); rail: collapsed dialer (opens to the right). */
  variant?: 'header' | 'rail';
}) {
  const [open, setOpen] = useState(false);
  const hydrateAutoAnswer = useDialerPrefsStore((s) => s.hydrateAutoAnswer);
  const setAutoAnswer = useDialerPrefsStore((s) => s.setAutoAnswer);
  const saving = useDialerPrefsStore((s) => s.saving);
  const savedAutoAnswer = useAgentAutoAnswer(agentId);

  useEffect(() => {
    if (agentId != null) hydrateAutoAnswer(agentId, serverAutoAnswer);
  }, [agentId, serverAutoAnswer, hydrateAutoAnswer]);

  const disabledReason = !hasWebrtcProfile ? WEBRTC_ONLY_MESSAGE : '';
  const usable = !disabledReason && agentId != null;
  // Effective state: only "on" while it can actually apply.
  const autoAnswer = usable && savedAutoAnswer;

  const isRail = variant === 'rail';

  // Only the setting itself is faded when unavailable; the reason stays at full strength.
  const faded = usable ? '' : 'opacity-50';

  const row = (
    <div className="rounded-xl px-2 py-2.5">
      <label
        htmlFor="dialer-auto-answer"
        aria-disabled={!usable}
        className={`flex items-start gap-3 ${usable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
      >
        <span
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${faded} ${
            autoAnswer ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'
          }`}
        >
          <PhoneIncoming size={15} />
        </span>
        <span className={`min-w-0 flex-1 ${faded}`}>
          <span className="flex items-center gap-2 text-sm font-medium text-foreground">
            Auto answer
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                autoAnswer ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-muted text-muted-foreground'
              }`}
            >
              {!usable ? 'Unavailable' : autoAnswer ? 'On' : 'Off'}
            </span>
          </span>
          <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
            All WebRTC calls should be auto-answered, with no Answer button
          </span>
        </span>
        <Switch
          id="dialer-auto-answer"
          checked={autoAnswer}
          disabled={!usable || saving}
          onCheckedChange={(on) => {
            if (agentId != null) void setAutoAnswer(agentId, on);
          }}
          className="mt-1"
        />
      </label>
      {disabledReason ? (
        <p className="ml-11 mt-2 flex items-start gap-1.5 rounded-lg border border-destructive/30 bg-destructive/10 px-2 py-1.5 text-[11px] font-medium leading-snug text-destructive">
          <Info size={12} className="mt-px shrink-0" />
          {disabledReason}
        </p>
      ) : null}
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={autoAnswer ? 'Dialer settings · Auto answer on' : 'Dialer settings'}
          aria-label="Dialer settings"
          className={`relative flex shrink-0 items-center justify-center border transition-colors ${
            isRail ? 'h-9 w-9 rounded-[10px]' : '-ml-1 h-6 w-6 rounded-lg'
          } ${
            autoAnswer
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15 dark:text-emerald-400'
              : open
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border/70 bg-muted/60 text-muted-foreground hover:border-foreground/30 hover:text-foreground'
          }`}
        >
          <Settings
            size={isRail ? 15 : 13}
            strokeWidth={2.25}
            className={`transition-transform duration-300 ${open ? 'rotate-90' : ''}`}
          />
          {autoAnswer ? (
            <span
              className={`absolute rounded-full bg-emerald-500 ring-2 ring-card ${
                isRail ? '-right-1 -top-1 h-2.5 w-2.5' : '-right-1 -top-1 h-2 w-2'
              }`}
              aria-hidden
            />
          ) : null}
        </button>
      </PopoverTrigger>

      <PopoverContent
        side={isRail ? 'right' : 'bottom'}
        align="start"
        sideOffset={8}
        className="w-72 rounded-2xl p-0 shadow-xl"
      >
        <div className="border-b border-border/60 px-4 py-3">
          <p className="text-sm font-semibold text-foreground">Dialer settings</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Saved to your agent profile.</p>
        </div>

        <div className="p-2">{row}</div>
      </PopoverContent>
    </Popover>
  );
}
